import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BookingCriterion, KindOfCriterion } from '../../model/booking-criterion.model';
import { Element } from '../../model/element.model';

interface BookingCriterionApiModel {
  booking_Criterion_ID: string;
  booking_Group_ID: string;
  element_ID: string;
  conversion_Table_ID: string | null;
  number: number;
  kindOf_Criterion: string;
  startPosition: number | null;
  numberOf_Positions: number | null;
  element_Name: string;
  active: boolean;
  numeric: boolean;
  list: boolean;
  conversion_Table_Name: string | null;
  changeUser?: string | null;
  changeDateTime?: string | null;
}

interface BookingCriterionApiPayload {
  booking_Criterion_ID: string;
  booking_Group_ID: string;
  element_ID: string;
  conversion_Table_ID: string | null;
  number: number;
  kindOf_Criterion: string;
  startPosition: number | null;
  numberOf_Positions: number | null;
  element_Name: string;
  active: boolean;
  numeric: boolean;
  list: boolean;
  conversion_Table_Name: string;
  changeUser: string;
}

interface ElementApiModel {
  element_ID: string;
  element_Name: string;
  rubric?: string;
  numeric: boolean;
  list: boolean;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class BookingCriterionService {
  private criterionUrl = environment.apiUrl + '/kbj-frontend/api/v1/BookingGroup/BookingCriterion';
  private elementUrl = environment.apiUrl + '/kbj-frontend/api/v1/Element';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all booking criteria for the given booking group.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getCriteriaByGroup(bookingGroupId: string): Observable<BookingCriterion[]> {
    return this.http
      .get<BookingCriterionApiModel[] | null>(
        this.criterionUrl + '/ByContext/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiPayloadToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new booking criterion. The criterion id is forced to the empty
   * guid. The number (1–8) must be set by the caller.
   */
  addCriterion(criterion: BookingCriterion): Observable<unknown> {
    const payload = this.mapModelToApiPayload({
      ...criterion,
      bookingCriterionId: EMPTY_GUID,
    });

    return this.http
      .post<unknown>(this.criterionUrl, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Updates an existing booking criterion.
   */
  updateCriterion(id: string, criterion: BookingCriterion): Observable<unknown> {
    const payload = this.mapModelToApiPayload({
      ...criterion,
      bookingCriterionId: id,
    });

    return this.http
      .put<unknown>(this.criterionUrl + '/' + id, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Removes a booking criterion by its id.
   */
  removeCriterion(id: string): Observable<unknown> {
    return this.http
      .delete(this.criterionUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Returns the elements that can be used as a criterion ("Criterium").
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getCriterionElements(): Observable<Element[]> {
    return this.http
      .get<ElementApiModel[] | null>(this.elementUrl + '/Criterion', this.httpOptions)
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => ({
            elementId: item.element_ID,
            elementName: item.element_Name,
            rubric: item.rubric,
            numeric: item.numeric,
            list: item.list,
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  // ------------------------------------------------------------------------------
  // Helper methods for mapping between API payloads and the BookingCriterion model.

  private mapApiPayloadToModel(apiModel: BookingCriterionApiModel): BookingCriterion {
    return {
      bookingCriterionId: apiModel.booking_Criterion_ID,
      bookingGroupId: apiModel.booking_Group_ID,
      elementId: apiModel.element_ID,
      elementName: apiModel.element_Name,
      conversionTableId: apiModel.conversion_Table_ID,
      conversionTableName: apiModel.conversion_Table_Name ?? '',
      number: apiModel.number,
      kindOfCriterion: apiModel.kindOf_Criterion as KindOfCriterion,
      startPosition: apiModel.startPosition,
      numberOfPositions: apiModel.numberOf_Positions,
      active: apiModel.active,
      numeric: apiModel.numeric,
      list: apiModel.list,
    };
  }

  private mapModelToApiPayload(model: BookingCriterion): BookingCriterionApiPayload {
    return {
      booking_Criterion_ID: model.bookingCriterionId ?? EMPTY_GUID,
      booking_Group_ID: model.bookingGroupId,
      element_ID: model.elementId,
      conversion_Table_ID: model.conversionTableId,
      number: model.number,
      kindOf_Criterion: model.kindOfCriterion,
      startPosition: model.startPosition,
      numberOf_Positions: model.numberOfPositions,
      element_Name: model.elementName,
      active: model.active,
      numeric: model.numeric,
      list: model.list,
      conversion_Table_Name: model.conversionTableName ?? '',
      changeUser: EMPTY_GUID,
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
